import React from "react";
import intl from "react-intl-universal";

import type {ClientInApp} from "../../../shared/types/SubscriptionTypes";

import {Button} from "../../components/Button";
import {CloseIcon} from "../../components/icons/CloseIcon";
import {MODALS} from "../../screens/modals/modal-names";
import {RocketLaunch} from "../../components/icons/RocketLaunchIcon";
import {Tooltip} from "../../components/Tooltip";
import {useAppState} from "@stores/AppStateContext";
import {dismissibleStorageStrategies, useDismissibleStorage} from "../../hooks/useDismissibleStorage";


import "./LayoutTrialInfoLine.css";


interface LayoutTrialInfoLineProps {
    client: ClientInApp;
    currentLocale: string;
}

const TRIAL_INFO_LINE_DISMISS_STRATEGY = dismissibleStorageStrategies.dailyAtHour(6);

const getFreeTrialEndDate = (client: ClientInApp) => {
    if (client.subscription_endsAt) {
        return new Date(client.subscription_endsAt);
    }

    const endsAt = new Date(client.createdAt);
    endsAt.setDate(endsAt.getDate() + 30);
    return endsAt;
};

const formatFreeTrialEndDate = (currentLocale: string, date: Date) => {
    return new Intl.DateTimeFormat(currentLocale, {
        year: "numeric",
        month: "long",
        day: "numeric",
    }).format(date);
};

export const LayoutTrialInfoLine = ({client, currentLocale}: LayoutTrialInfoLineProps) => {
    const appState = useAppState();
    const freeTrialEndDate = getFreeTrialEndDate(client);
    const freeTrialEndDateLabel = formatFreeTrialEndDate(currentLocale, freeTrialEndDate);
    const {isHidden, hide} = useDismissibleStorage({
        storageKey: `LayoutTrialInfoLine:${client.id}`,
        strategy: TRIAL_INFO_LINE_DISMISS_STRATEGY,
    });

    const continueUsingLogycore = intl.get("layout.trial_info_line.action_link").d("continue using Logycore");
    const continueUsingTooltip = intl.getHTML("layout.trial_info_line.action_link.tooltip")
        .d(`If you decide not to continue, you can export your content during the trial.<br/>If you have a lot of content, our support team can help you export everything at once`);

    const ContinueUsingLogycore = (
        <Tooltip title={continueUsingTooltip} maxWidth={360}>
            <span
                className="LayoutTrialInfoLine__inlineLink"
            >
                {continueUsingLogycore}
            </span>
        </Tooltip>
    );

    if (isHidden) {
        return null;
    }

    return (
        <div className="LayoutTrialInfoLine">
            <div className="LayoutTrialInfoLine__content">
                <div className="LayoutTrialInfoLine__main">
                    <div className="LayoutTrialInfoLine__messageRow">
                        <RocketLaunch className="LayoutTrialInfoLine__icon" size={20}/>
                        <p className="LayoutTrialInfoLine__message body-medium">
                            {intl.getHTML("layout.trial_info_line.message", {freeTrialEndDateLabel, ContinueUsingLogycore}).d(`<span>Freemium plan is no longer available. You're now on a <span className="LayoutTrialInfoLine__strong">30-day Free Trial</span> with full access until <span className="LayoutTrialInfoLine__strong">{freeTrialEndDateLabel}</span>. Please subscribe to {ContinueUsingLogycore}</span>`)}
                        </p>
                    </div>
                    <Button
                        className="LayoutTrialInfoLine__button"
                        onClick={() => appState.setModal({name: MODALS.CHECKOUT, params: undefined})}
                        size="sm"
                        style="soft"
                        text={intl.get("layout.trial_info_line.pricing").d("View Pricing")}
                        type="neutral"
                    />
                </div>
                <button
                    className="LayoutTrialInfoLine__closeButton"
                    onClick={hide}
                    type="button"
                >
                    <CloseIcon size={20}/>
                </button>
            </div>
        </div>
    );
};
